import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  KeyRound, Plus, Loader2, ShieldAlert, Trash2, CircleDot, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = [
  ["door_key", "Door / mailbox key"], ["mailbox_key", "Mailbox key"], ["building_fob", "Building fob"],
  ["gate_remote", "Gate remote"], ["garage_remote", "Garage remote"], ["parking_permit", "Parking permit"],
  ["storage_access", "Storage access"], ["package_locker", "Package-locker access"],
  ["amenity_access", "Pool / gym / amenity access"], ["other", "Other access item"],
];
const TYPE_LABEL = Object.fromEntries(ITEM_TYPES);
const STATUSES = [
  ["received", "Recorded as received"], ["replaced", "Recorded as replaced"],
  ["not_working", "Recorded as not working"], ["lost", "Recorded as lost"], ["returned", "Recorded as returned"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);
const STATUS_CLR = {
  received: "bg-[#DCFCE7] text-[#15803D]", replaced: "bg-[#DBEAFE] text-[#1D4ED8]",
  not_working: "bg-[#FEF3C7] text-[#92400E]", lost: "bg-[#FEE2E2] text-[#B91C1C]",
  returned: "bg-[#E0E7FF] text-[#4338CA]",
};
const fmt = (d) => (d ? d : "no date");

export function AccessAmenityRecord({ propertyId, archived }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [eventFor, setEventFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/access-items`);
      setItems(data.items || []);
    } catch { /* handled by parent */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const removeItem = async (id) => {
    try { await api.delete(`/access-items/${id}`); toast.success("Item removed from your list."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that item."); }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="access-amenity-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound size={18} className="text-brand shrink-0" />
          <h3 className="font-heading text-lg font-semibold text-navy">Access &amp; amenity record</h3>
          <span className="text-[10px] font-bold uppercase tracking-wide text-[#15803D] bg-[#DCFCE7] rounded-full px-2 py-0.5">Free</span>
        </div>
        {!archived && (
          <Button size="sm" className="rounded-lg" data-testid="access-add-btn" onClick={() => setAddOpen(true)}>
            <Plus size={14} className="mr-1.5" /> Add item
          </Button>
        )}
      </div>
      <p className="mt-2 text-sm text-[#475569]">
        Keep a simple, private log of keys, fobs, permits and amenity access for this tenancy — and note when each was
        received, replaced, stopped working, lost, or returned.
      </p>
      <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#FEF3C7] border border-[#FDE68A] px-3 py-2 text-xs text-[#92400E]" data-testid="access-privacy-warning">
        <ShieldAlert size={15} className="mt-0.5 shrink-0" />
        <span>Don't photograph or type access codes, PINs, key-cut patterns, or full credential/barcode numbers — a photo of a key or code could let someone copy it. Use a short label instead.</span>
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-[#94A3B8]" data-testid="access-empty">No access items yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((it) => (
            <li key={it.item_id} data-testid={`access-item-${it.item_id}`} className="rounded-xl border border-[#E2E8F0] p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-navy truncate">{it.label}</p>
                  <p className="text-xs text-[#64748B]">{TYPE_LABEL[it.item_type] || it.item_type}{it.partial_identifier ? ` · ${it.partial_identifier}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {it.latest_status
                    ? <span data-testid={`access-status-${it.item_id}`} className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${STATUS_CLR[it.latest_status]}`}>{STATUS_LABEL[it.latest_status]}</span>
                    : <span data-testid={`access-status-${it.item_id}`} className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-[#F1F5F9] text-[#64748B]">No outcome recorded</span>}
                  {!archived && (
                    <button onClick={() => removeItem(it.item_id)} data-testid={`access-remove-${it.item_id}`} className="text-[#94A3B8] hover:text-red-500" aria-label="Remove"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
              {it.events.length > 0 && (
                <ol className="mt-3 space-y-1.5 border-l-2 border-[#E2E8F0] pl-3">
                  {it.events.map((e) => (
                    <li key={e.event_id} className="text-sm flex items-start gap-2">
                      <CircleDot size={12} className="mt-1 text-brand shrink-0" />
                      <span className="text-[#334155]">
                        <span className="font-medium">{STATUS_LABEL[e.status]}</span>
                        <span className="text-[#94A3B8]"> · {fmt(e.event_date)}</span>
                        {e.note && <span className="block text-xs text-[#64748B]">{e.note}</span>}
                        {e.photo_ids.length > 0 && <span className="block text-[10px] text-[#94A3B8]">{e.photo_ids.length} photo(s) linked</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {!archived && (
                <Button size="sm" variant="outline" className="mt-3 rounded-lg text-xs h-8" data-testid={`access-add-event-${it.item_id}`} onClick={() => setEventFor(it)}>
                  Record an update <ChevronRight size={13} className="ml-0.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {addOpen && <AddItemDialog propertyId={propertyId} open={addOpen} onOpenChange={setAddOpen} onAdded={() => { setAddOpen(false); load(); }} />}
      {eventFor && <AddEventDialog item={eventFor} open={!!eventFor} onOpenChange={(o) => !o && setEventFor(null)} onAdded={() => { setEventFor(null); load(); }} />}
    </div>
  );
}

function AddItemDialog({ propertyId, open, onOpenChange, onAdded }) {
  const [type, setType] = useState("door_key");
  const [label, setLabel] = useState("");
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!label.trim()) { toast.error("Give the item a label."); return; }
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/access-items`, { item_type: type, label: label.trim(), partial_identifier: pid.trim() || null });
      toast.success("Item added.");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add that item."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="access-add-dialog">
        <DialogHeader>
          <DialogTitle>Add an access item</DialogTitle>
          <DialogDescription>Use a short label — never a code, PIN, or full credential number.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="access-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ITEM_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="acc-label">Label</Label>
            <Input id="acc-label" data-testid="access-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Main lobby fob" className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs" htmlFor="acc-pid">Short identifier (optional)</Label>
            <Input id="acc-pid" data-testid="access-pid-input" value={pid} onChange={(e) => setPid(e.target.value)} placeholder="e.g. blue tag" className="mt-1 h-10 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-xl" disabled={busy} data-testid="access-save-item" onClick={save}>
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Plus size={15} className="mr-1.5" />} Add item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddEventDialog({ item, open, onOpenChange, onAdded }) {
  const [status, setStatus] = useState("received");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/access-items/${item.item_id}/events`, { status, event_date: date.trim() || null, note: note.trim() || null });
      toast.success("Update recorded.");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record that update."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="access-event-dialog">
        <DialogHeader>
          <DialogTitle>Record an update — {item.label}</DialogTitle>
          <DialogDescription>This records what you observed. TenantSeal doesn't verify issuance, function, or return.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">What happened?</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="access-event-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="acc-date">Date (optional)</Label>
            <Input id="acc-date" data-testid="access-event-date" value={date} onChange={(e) => setDate(e.target.value)} placeholder="e.g. 2026-06-01" className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs" htmlFor="acc-note">Note (optional)</Label>
            <Input id="acc-note" data-testid="access-event-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note — no codes or PINs" className="mt-1 h-10 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-xl" disabled={busy} data-testid="access-save-event" onClick={save}>
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Record update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
